import {
  AggregateAwaAnalysis,
  Analysis,
  AnalysisTriplet,
  DynamicSleeveDef,
  PortfolioDisplayOption,
  ProposalAwaAnalysis,
  TotalPortfolioAnalysis,
} from '@blk/aw-bff-typescript-models';
import { isEmpty, isNumber, isUndefined } from 'lodash';
import { ExhibitError } from './ExhibitError';

export const COMBINED_CUSIP_PREFIX = 'COMBINED_';
export const NO_CUSIP_PLACEHOLDER = '_NO_CUSIP';

// TODO import from aw-exhibits when utils are moved
export type RenderableOutputKey = 'original' | 'proposed' | 'benchmark' | 'model';

export interface RenderableOutput {
  isEmpty: boolean;
  key: RenderableOutputKey;
}

// needed for Comparisons 5 id exihibit support
export interface RenderableOutputV2 {
  color: string;
  currencySymbol: string;
  displayName: string;
  id: string;
  idType: string;
  key: RenderableOutputKey;
  portfolioID?: string;
}

export interface ProposalAwaAnalysisForHoldingsTable {
  original: AggregateAwaAnalysis;
  proposed?: AggregateAwaAnalysis;
}

export const selectTotalPortfolioAnalysisFromProposalAnalyst = (
  proposalAwaAnalysis: ProposalAwaAnalysis | ProposalAwaAnalysisForHoldingsTable,
  portfolioId: string,
  currentOrProposed: keyof ProposalAwaAnalysis
): TotalPortfolioAnalysis | undefined => {
  if (!proposalAwaAnalysis[currentOrProposed]) {
    return undefined;
  }

  // look for top level aggregates
  if (portfolioId.includes(':')) {
    if (portfolioId === proposalAwaAnalysis[currentOrProposed]?.aggregate?.portfolioID) {
      return proposalAwaAnalysis[currentOrProposed]?.aggregate;
    }
  }
  // find the portfolio first if does not have tag

  const portfolioAnalysis = proposalAwaAnalysis[currentOrProposed]?.portfolios?.find(
    (analysis) => analysis.portfolioID === portfolioId
  );
  if (portfolioAnalysis) {
    return portfolioAnalysis;
  }

  // look for mid level aggregates

  const portfolioIdWithNoTag = portfolioId?.includes(':') ? portfolioId.split(':')[1] : portfolioId;
  if (portfolioIdWithNoTag === proposalAwaAnalysis[currentOrProposed]?.aggregate?.portfolioID) {
    return proposalAwaAnalysis[currentOrProposed]?.aggregate;
  }

  const midLevelMatch = proposalAwaAnalysis[currentOrProposed]?.midLevelAggregates?.find(
    (mla) => mla.analysis.portfolioID === portfolioIdWithNoTag
  );
  if (midLevelMatch) {
    return midLevelMatch.analysis;
  }

  // Return undefined if all conditions fail
  return undefined;
};

export const extractAnalysisTripletFromDynamicSleeveDef = (
  tpa: TotalPortfolioAnalysis,
  dynamicSleeveDef: DynamicSleeveDef,
  sleeve: string
): AnalysisTriplet => {
  const ErrorMsg = `Could not find dynamic sleeve analysis for ${dynamicSleeveDef.tag}:${dynamicSleeveDef.value}`;
  const sleeveGroup = tpa.dynamicSleeves.find((sleeveGroup) => sleeveGroup.tag === dynamicSleeveDef.tag);
  if (!sleeveGroup) {
    throw Error(ErrorMsg);
  }
  const sleeveWithAnalysis = sleeveGroup.sleeves.find((sleeve) => {
    if (dynamicSleeveDef.subSleeve) {
      return sleeve.tag === dynamicSleeveDef.subSleeve.tag && sleeve.value === dynamicSleeveDef.subSleeve.value;
    }
    return sleeve.tag === dynamicSleeveDef.tag && sleeve.value === dynamicSleeveDef.value;
  });
  if (!sleeveWithAnalysis) {
    throw Error(ErrorMsg);
  }
  let result = sleeveWithAnalysis.sleeveAnalysis;
  if (sleeve !== 'total' && !isEmpty(sleeveWithAnalysis.sleeves?.[sleeve])) {
    result = sleeveWithAnalysis.sleeves[sleeve];
  }
  return result;
};

export const extractAnalysisTripletFromTotalPortfolioAnalysis = (
  tpa: TotalPortfolioAnalysis,
  sleeve: string = 'total',
  renderableOutputKey?: RenderableOutputKey
): AnalysisTriplet => {
  if (renderableOutputKey === 'model') {
    const analysis = tpa.totalPortfoliosVsOtherReferencePortfolios.find(
      (tpa) => tpa.referencePortfolioTag === 'model'
    )?.totalPortfolio;
    if (analysis) {
      return analysis;
    } else {
      throw Error('Could not find model analysis');
    }
  }
  switch (sleeve) {
    case 'total':
      return tpa.totalPortfolio;
    default:
      if (tpa.sleeves?.[sleeve]) {
        return tpa.sleeves[sleeve];
      }
      throw Error('Analysis is missing sleeves');
  }
};

export const extractAnalysisFromProposalAnalystForRenderableOutput = (
  proposalAwaAnalysis: ProposalAwaAnalysis,
  portfolioId: string,
  renderableOutputKey: RenderableOutputKey,
  sleeve: string = 'total',
  active: boolean = false,
  dynamicSleeve?: DynamicSleeveDef,
  modelPortfolioId?: string
): Analysis => {
  const currentOrProposed =
    renderableOutputKey === 'benchmark' || renderableOutputKey === 'model' ? 'proposed' : renderableOutputKey;
  const tpa = selectTotalPortfolioAnalysisFromProposalAnalyst(proposalAwaAnalysis, portfolioId, currentOrProposed);
  if (!tpa) {
    throw Error(`Could not find total portfolio analysis for ${portfolioId}`);
  }
  const triplet =
    !isUndefined(dynamicSleeve) && !isEmpty(dynamicSleeve)
      ? extractAnalysisTripletFromDynamicSleeveDef(tpa, dynamicSleeve, sleeve)
      : extractAnalysisTripletFromTotalPortfolioAnalysis(tpa, sleeve, renderableOutputKey);
  if (renderableOutputKey === 'benchmark') {
    if (!triplet.benchmark) {
      throw Error(`ProposalAnalyst response missing benchmark for ${portfolioId}`);
    }
    if (active) {
      throw Error(`Cannot show active analysis on a benchmark`);
    }
    return triplet.benchmark;
  }
  if (renderableOutputKey === 'model') {
    const modelReference = tpa.totalPortfoliosVsOtherReferencePortfolios?.find(
      (ref) => ref.referencePortfolioTag === 'model' && ref.referencePortfolioId === modelPortfolioId
    );
    if (!modelReference?.totalPortfolio?.benchmark) {
      throw Error(`ProposalAnalyst response missing model for ${portfolioId}`);
    }
    if (active) {
      throw Error(`Cannot show active analysis on a model`);
    }
    // support for sleeve to be done in future PR
    const modelTriplet = modelReference.totalPortfolio;
    if (!modelTriplet.benchmark) {
      throw Error(`Model analysis missing benchmark data for ${portfolioId}`);
    }
    return modelTriplet.benchmark;
  }
  if (active) {
    if (!triplet.active) {
      throw new ExhibitError('Account does not have a set benchmark', 'c_noBenchmark');
    }
    return triplet.active;
  }
  return triplet.portfolio;
};

export const getColorByPortfolio = (portfoliosDisplay: { [index: string]: PortfolioDisplayOption }, key: string) =>
  portfoliosDisplay[key].color || '';

export const getNameByPortfolio = (portfoliosDisplay: { [index: string]: PortfolioDisplayOption }, key: string) =>
  portfoliosDisplay[key].name || '';

export interface EnhancedPositionContributor {
  combined?: boolean;
  contribution: number;
  cusip: string;
  diversification?: number;
  positionID: string;
  riskOfTotal?: number;
  securityCorrelation?: number;
  securityStandalone?: number;
  sleeveOverrideLabel?: string;
  sleeveValue?: string;
  standalone?: number;
  weight?: number;
}

/**
 * Function will take in a list of EnhancedPositionContributor objects and combine them using cusip as the group-by.
 */
export const combinePositionContributors = (
  securityContributors: EnhancedPositionContributor[], // set of contributors
  selector: (contributor: EnhancedPositionContributor) => string
): EnhancedPositionContributor[] => {
  const combinationsByCusip: { [combinedValue: string]: EnhancedPositionContributor } = {};
  securityContributors.forEach((sc) => {
    const combinedValue = selector(sc);
    const { contribution, riskOfTotal, standalone, weight, diversification, cusip, sleeveOverrideLabel, sleeveValue } =
      sc;
    if (combinationsByCusip[combinedValue]) {
      combinationsByCusip[combinedValue] = {
        ...combinationsByCusip[combinedValue],
        /**
         * Some of these values can be undefined, so we add them together if we can. If they are undefined in all,
         * we leave them as undefined. If one is undefined and one is not, we set the undefined one as 0
         */
        securityStandalone:
          sleeveOverrideLabel || sleeveValue ? undefined : combinationsByCusip[combinedValue].securityStandalone,
        securityCorrelation:
          sleeveOverrideLabel || sleeveValue ? undefined : combinationsByCusip[combinedValue].securityCorrelation,
        riskOfTotal:
          isNumber(combinationsByCusip[combinedValue].riskOfTotal) || isNumber(riskOfTotal)
            ? (combinationsByCusip[combinedValue].riskOfTotal || 0) + (riskOfTotal || 0)
            : undefined,
        standalone:
          isNumber(combinationsByCusip[combinedValue].standalone) || isNumber(standalone)
            ? (combinationsByCusip[combinedValue].standalone || 0) + (standalone || 0)
            : undefined,
        weight:
          isNumber(combinationsByCusip[combinedValue].weight) || isNumber(weight)
            ? (combinationsByCusip[combinedValue].weight || 0) + (weight || 0)
            : undefined,
        diversification:
          isNumber(combinationsByCusip[combinedValue].diversification) || isNumber(diversification)
            ? (combinationsByCusip[combinedValue].diversification || 0) + (diversification || 0)
            : undefined,
        contribution: combinationsByCusip[combinedValue].contribution + contribution,
        positionID: COMBINED_CUSIP_PREFIX + combinedValue,
        cusip: combinationsByCusip[combinedValue].cusip === cusip ? cusip : NO_CUSIP_PLACEHOLDER,
        sleeveOverrideLabel,
        combined: true,
      };
    } else {
      combinationsByCusip[combinedValue] = sc;
    }
  });
  return Object.values(combinationsByCusip);
};

const displayWithoutMultiplier: { [key: string]: boolean } = {
  duration: true,
  twelveMonthTrailingYield: true,
  securityCorrelation: true,
  proposalSupplementPositionFee: true,
  esgScore: true,
};

// Scale all contributions returned by a 100x multiplier
export const applyMultiplierToContribution = (name: string, contribution: number) =>
  // for dynamic col selectors (e.g. esg contributors), the selector name would follow the pattern: {selectorName}_{field}; this is required as selector names are expected to be unique when building the data
  displayWithoutMultiplier[name.split('_')[0]] ? contribution : 100 * contribution;

// Revert the 100x scale for calculations where the multiplier should be removed
export const revertMultiplierToContribution = (name: string, contribution: number) =>
  // for dynamic col selectors (e.g. esg contributors), the selector name would follow the pattern: {selectorName}_{field}; this is required as selector names are expected to be unique when building the data
  displayWithoutMultiplier[name.split('_')[0]] ? contribution : contribution / 100;
